import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Sparkle, ChartLineUp, ShieldCheck, Lightning, Broadcast, Users, Trophy, Globe, ArrowRight, Check, CaretDown, Sun, Moon } from '@phosphor-icons/react';
import { useState } from 'react';
import { TVTickerTape } from '@/components/app/TradingView';
import { useTheme } from '@/context/ThemeContext';

const stats = [
  { label: 'Active Traders', value: '48,200+', icon: Users },
  { label: 'Signals Broadcast', value: '12,540', icon: Broadcast },
  { label: 'Avg Win Rate', value: '82%', icon: Trophy },
  { label: 'Countries', value: '120+', icon: Globe },
];

const features = [
  { icon: Broadcast, title: 'Live Trading Signals', desc: 'Real-time BUY/SELL alerts with entry, SL, TP1/2/3 and full analysis — instantly delivered to your dashboard, Telegram & WhatsApp.' },
  { icon: ChartLineUp, title: 'TradingView Integrated', desc: 'Live embedded charts, heatmaps, screeners and economic calendar — professional-grade market intelligence.' },
  { icon: ShieldCheck, title: 'Verified Broker Access', desc: 'Unlock premium via broker verification or subscription. Complete transparency with an audited signal history.' },
  { icon: Lightning, title: 'Instant Notifications', desc: 'Push, browser, Telegram & WhatsApp — never miss a signal or market-moving event again.' },
];

const testimonials = [
  { name: 'Rahul Verma', role: 'Prop Trader, Mumbai', text: 'The signal quality is unreal. I doubled my account in 6 weeks following 4xview alerts.', avatar: 'RV' },
  { name: 'Sarah Lin', role: 'FX Trader, Singapore', text: 'The dashboard feels like Bloomberg Terminal but modernized. Everything I need in one place.', avatar: 'SL' },
  { name: 'Diego Martinez', role: 'Crypto Investor, Madrid', text: 'Broker verification is a genius model. Real skin in the game, real accountability.', avatar: 'DM' },
];

const plans = [
  { name: 'Monthly', price: 49, period: '/mo', features: ['Live signals', 'Full analysis', 'Live chat', 'Telegram + WhatsApp', 'Economic calendar'] },
  { name: 'Quarterly', price: 129, period: '/3mo', features: ['Everything in Monthly', 'Priority support', '12% savings', 'Weekly deep-dives'], popular: false },
  { name: 'Yearly', price: 399, period: '/yr', features: ['Everything in Quarterly', 'Private community', '32% savings', '1-on-1 review'], popular: true },
  { name: 'Lifetime', price: 999, period: 'once', features: ['Everything, forever', 'VIP status', 'Founding member perks', 'Lifetime updates'] },
];

const faqs = [
  { q: 'How do the live signals work?', a: 'Our senior analysts publish precise entries, stop-losses and 3 take-profit levels for forex, crypto, gold and indices. Every signal is instantly pushed to your dashboard and connected channels.' },
  { q: 'What is broker verification?', a: 'You can unlock Premium by opening a live trading account with our partner brokers. Submit your account UID + screenshot and our team verifies within 24 hours — no subscription fee required.' },
  { q: 'Can I cancel my subscription?', a: 'Yes, anytime. Monthly and quarterly plans can be cancelled from your Profile. Lifetime is a one-time purchase.' },
  { q: 'Do you offer refunds?', a: 'We offer a 7-day money-back guarantee on all monthly plans.' },
  { q: 'Is my data secure?', a: 'Absolutely. We use bank-grade JWT encryption, encrypted screenshots storage and never share your data with third parties.' },
];

export default function Landing() {
  const nav = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const { theme, toggle } = useTheme();

  return (
    <div className="min-h-screen bg-surface-base relative overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass-strong">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-9 h-9 rounded-lg gradient-orange flex items-center justify-center shadow-glow-saffron">
              <Sparkle size={20} weight="fill" className="text-white" />
            </div>
            <span className="font-heading font-bold text-xl">4xview</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="text-muted-foreground hover:text-white transition-colors">Pricing</a>
            <a href="#testimonials" className="text-muted-foreground hover:text-white transition-colors">Traders</a>
            <a href="#faq" className="text-muted-foreground hover:text-white transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggle} data-testid="landing-theme-toggle" aria-label="Toggle theme" className="w-10 h-10 rounded-md hover:bg-white/5 flex items-center justify-center transition-colors">
              {theme === 'dark' ? <Sun size={18} weight="duotone" className="text-saffron" /> : <Moon size={18} weight="duotone" className="text-saffron" />}
            </button>
            <Button variant="ghost" onClick={() => nav('/login')} data-testid="nav-login-btn" className="text-white hover:bg-white/5">Sign in</Button>
            <Button onClick={() => nav('/signup')} data-testid="nav-signup-btn" className="bg-saffron hover:bg-saffron-hover text-white shadow-glow-saffron">Get Started</Button>
          </div>
        </div>
      </nav>

      {/* Ticker */}
      <div className="pt-16 border-b border-white/5 glass">
        <TVTickerTape />
      </div>

      {/* Hero */}
      <section className="relative pt-24 pb-32 px-6">
        <div className="hero-glow" style={{ top: -100, left: -100 }} />
        <div className="hero-glow" style={{ bottom: -200, right: -100 }} />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div initial={{opacity:0, y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-saffron/30 mb-6">
                <span className="w-2 h-2 rounded-full bg-saffron animate-pulse" />
                <span className="text-xs text-saffron uppercase tracking-[0.2em] font-semibold">Live Market Terminal</span>
              </div>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-6">
                Trade like the <span className="gradient-text">1%.</span><br />
                Signals engineered by <br />
                <span className="text-saffron">real market veterans.</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                4xview is the premium fintech terminal for forex, crypto, gold & indices traders. Live signals, TradingView-powered charts, verified broker access — all in one obsessively designed platform.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" onClick={() => nav('/signup')} data-testid="hero-cta-primary" className="bg-saffron hover:bg-saffron-hover text-white h-12 px-8 shadow-glow-saffron">
                  Start Free — No Card Required <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => nav('/login')} data-testid="hero-cta-secondary" className="h-12 px-8 border-white/20 bg-white/5 hover:bg-white/10 text-white">
                  View Live Signals
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-10 text-xs text-muted-foreground">
                <div className="flex -space-x-2">
                  {['R','S','D','M','A'].map((c,i) => (
                    <div key={i} className={`w-8 h-8 rounded-full border-2 border-surface-base flex items-center justify-center text-xs font-bold text-white ${i%2 ? 'bg-saffron' : 'bg-surface-2'}`}>{c}</div>
                  ))}
                </div>
                <span>Joined by <b className="text-white">48,200+ traders</b> worldwide</span>
              </div>
            </motion.div>

            {/* Right visual */}
            <motion.div initial={{opacity:0, scale:0.95}} animate={{opacity:1,scale:1}} transition={{duration:0.7, delay:0.2}} className="lg:col-span-5 relative">
              <div className="relative glass-strong rounded-2xl p-6 neon-border shadow-card-lift">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">BTC/USDT</div>
                    <div className="font-mono text-3xl font-bold mt-1">94,521.<span className="text-saffron">08</span></div>
                  </div>
                  <div className="text-right">
                    <div className="text-signal-buy font-mono text-lg font-bold" style={{color: '#2EA043'}}>+3.24%</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">24h</div>
                  </div>
                </div>
                <div className="h-40 chart-bg rounded-md relative overflow-hidden mb-4">
                  <svg className="w-full h-full" viewBox="0 0 400 160" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF6A00" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#FF6A00" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,120 L40,100 L80,110 L120,80 L160,90 L200,60 L240,70 L280,45 L320,55 L360,30 L400,40 L400,160 L0,160 Z" fill="url(#grad)" />
                    <path d="M0,120 L40,100 L80,110 L120,80 L160,90 L200,60 L240,70 L280,45 L320,55 L360,30 L400,40" fill="none" stroke="#FF6A00" strokeWidth="2" style={{filter:'drop-shadow(0 0 4px #FF6A00)'}} />
                  </svg>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="glass p-3 rounded"><div className="data-label">Entry</div><div className="font-mono font-bold mt-1">94,200</div></div>
                  <div className="glass p-3 rounded"><div className="data-label">SL</div><div className="font-mono font-bold mt-1 text-red-400">93,500</div></div>
                  <div className="glass p-3 rounded"><div className="data-label">TP1</div><div className="font-mono font-bold mt-1 text-green-400">95,400</div></div>
                </div>
                <div className="mt-4 flex items-center justify-between p-3 rounded-md bg-green-500/10 border border-green-500/20">
                  <span className="text-xs uppercase tracking-widest text-green-400 font-semibold">BUY Signal</span>
                  <span className="text-xs font-mono text-white">+124 pips</span>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full gradient-orange blur-3xl opacity-40 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/5 glass">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map(({label, value, icon: I}) => (
            <div key={label} className="text-center">
              <I size={28} className="text-saffron mx-auto mb-3" weight="duotone" />
              <div className="font-mono font-black text-3xl md:text-4xl gradient-text">{value}</div>
              <div className="data-label mt-2">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="data-label text-saffron mb-3">Features</div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">Everything a pro trader needs</h2>
            <p className="text-muted-foreground">One terminal. Every market. Every signal. Custom-designed for traders who take their edge seriously.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map(({icon: I, title, desc}, i) => (
              <motion.div key={title} initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}
                className="glass p-8 rounded-xl neon-border group">
                <div className="w-12 h-12 rounded-lg bg-saffron/10 border border-saffron/30 flex items-center justify-center mb-5 group-hover:bg-saffron/20 transition-colors">
                  <I size={24} className="text-saffron" weight="duotone" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-3">{title}</h3>
                <p className="text-muted-foreground leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="data-label text-saffron mb-3">Testimonials</div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold">Loved by serious traders</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t,i) => (
              <div key={i} className="glass p-8 rounded-xl neon-border">
                <div className="flex text-saffron mb-4">{'★★★★★'}</div>
                <p className="mb-6 leading-relaxed">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full gradient-orange flex items-center justify-center font-bold">{t.avatar}</div>
                  <div>
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="data-label text-saffron mb-3">Pricing</div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">Choose your edge</h2>
            <p className="text-muted-foreground">Or unlock premium free — verify a broker account with our partners.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {plans.map((p) => (
              <div key={p.name} className={`glass p-8 rounded-xl relative ${p.popular ? 'neon-border border-saffron/60 shadow-glow-saffron' : 'neon-border'}`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 gradient-orange text-white text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1 rounded-full">Popular</div>}
                <div className="data-label">{p.name}</div>
                <div className="mt-3 mb-6">
                  <span className="font-mono text-5xl font-black">${p.price}</span>
                  <span className="text-muted-foreground ml-1">{p.period}</span>
                </div>
                <ul className="space-y-3 mb-8 text-sm">
                  {p.features.map(f => <li key={f} className="flex items-start gap-2"><Check size={16} className="text-saffron mt-0.5 shrink-0" weight="bold" />{f}</li>)}
                </ul>
                <Button onClick={() => nav('/signup')} data-testid={`pricing-${p.name.toLowerCase()}-btn`} className={`w-full ${p.popular ? 'bg-saffron hover:bg-saffron-hover text-white' : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'}`}>
                  Get {p.name}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <div className="data-label text-saffron mb-3">FAQ</div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold">Questions? We've got answers.</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f,i) => (
              <div key={i} className="glass rounded-lg overflow-hidden border border-white/5">
                <button data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full p-5 flex items-center justify-between text-left hover:bg-white/5">
                  <span className="font-heading font-semibold">{f.q}</span>
                  <CaretDown size={18} className={`text-saffron transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-muted-foreground text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md gradient-orange flex items-center justify-center"><Sparkle size={14} weight="fill" /></div>
            <span className="font-heading font-bold text-white">4xview</span>
            <span className="ml-2">© 2026 All rights reserved.</span>
          </div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
